import { Injectable } from '@angular/core';
import { BookService } from '@studiobuki/web-core/lib/book-common';
import type { TCreateUserBookOmitKeys } from '@studiobuki/web-core/lib/firebase';
import { FirebaseService } from 'src/app/services/firebase.service';
import type { TBookMode } from '@studiobuki/web-core/lib/routing';
import { ROUTE_PATH_QUERY_PARAM_MODE_USER } from '@studiobuki/web-core/lib/routing';
import { LoaderService } from '@studiobuki/web-core/lib/loader';
import { Logger } from '@shared/common/logger';
import {
  INPUT_VALUE_STEP_CHARACTER,
  INPUT_VALUE_STEP_COVER,
  INPUT_VALUE_STEP_CHILDS_PREFERENCES,
  INPUT_VALUE_STEP_PHOTO_AND_MESSAGE,
  INPUT_VALUE_COVER_COLOR_ORANGE,
} from '@shared/common/product';
import { PHOTO_TOKYO_METRO_THIS_IS_MY_DREAM_LINE } from '@shared/jp/product';
import type { IBookThisIsMyDreamLineData } from '@shared/jp/models';
import ThisIsMyDreamLine from 'src/app/data/books/ThisIsMyDreamLine';
import { BookPagesThisIsMyDreamLineService } from './book-pages-this-is-my-dream-line.service';
import { getKidstitle } from './segments';

type TBookThisIsMyDreamLineCreateData = Pick<
  IBookThisIsMyDreamLineData,
  | 'heroName'
  | 'kunChan'
  | 'gender'
  | 'skinColor'
  | 'hairColor'
  | 'hairStyle'
  | 'clothColor'
  | 'glasses'
  | 'childLikes'
>;

const log = new Logger('BookThisIsMyDreamLineService');

@Injectable({
  providedIn: 'root',
})
export class BookThisIsMyDreamLineService extends BookService<
  IBookThisIsMyDreamLineData,
  TBookThisIsMyDreamLineCreateData
> {
  constructor(
    protected override _firebaseService: FirebaseService,
    protected override _bookPagesService: BookPagesThisIsMyDreamLineService,
    private _loaderService: LoaderService,
  ) {
    super(_firebaseService, _bookPagesService);
  }

  /** a method to create initial book data */
  // eslint-disable-next-line class-methods-use-this
  protected override _createBookData<
    O extends Partial<IBookThisIsMyDreamLineData>,
  >(
    /** data to create the book from */
    createBookData: TBookThisIsMyDreamLineCreateData,
    /** book data to owerride the defaults */
    overrideBookData: O,
  ) {
    const { kunChan } = createBookData;
    const kidstitle = getKidstitle(kunChan);
    const newBook: Omit<IBookThisIsMyDreamLineData, TCreateUserBookOmitKeys> = {
      ...createBookData,
      type: 'book',
      steps: {
        [INPUT_VALUE_STEP_CHARACTER]: {
          stepType: INPUT_VALUE_STEP_CHARACTER,
          isDone: true,
        },
        [INPUT_VALUE_STEP_COVER]: {
          stepType: INPUT_VALUE_STEP_COVER,
          isDone: true,
        },
        [INPUT_VALUE_STEP_CHILDS_PREFERENCES]: {
          stepType: INPUT_VALUE_STEP_CHILDS_PREFERENCES,
          isDone: true,
        },
        [INPUT_VALUE_STEP_PHOTO_AND_MESSAGE]: {
          stepType: INPUT_VALUE_STEP_PHOTO_AND_MESSAGE,
          isDone: false,
        },
      },
      multiFiles: {
        [PHOTO_TOKYO_METRO_THIS_IS_MY_DREAM_LINE]: {
          fileUploadStatus: 0,
        },
      },
      alias: ThisIsMyDreamLine.alias,
      cover: ThisIsMyDreamLine.option,
      coverColor: INPUT_VALUE_COVER_COLOR_ORANGE,
      messageText:
        `でんしゃは　いろんなばしょに　つれていってくれるね\n` +
        `じんせいも　でんしゃみたいだよ。\n` +
        `ときには　とっきゅうみたいに　はやくすすんだり\n` +
        `ときには　かくえきていしゃみたいに\n` +
        `ゆっくりけしきを　たのしんだり。\n` +
        `どんなみちを　えらんでも\n` +
        `${createBookData.heroName}${kidstitle}の　だいすきなことを　たいせつにしてね。`,
    };

    return { ...newBook, ...overrideBookData };
  }

  override async loadBook(
    bookId: string,
    onBookDataLoad:
      | ((bookData: IBookThisIsMyDreamLineData) => Promise<void>)
      | ((bookData: IBookThisIsMyDreamLineData) => void),
    mode: TBookMode = ROUTE_PATH_QUERY_PARAM_MODE_USER,
  ) {
    const bookData = await super.loadBook(bookId, onBookDataLoad, mode);

    this._bookPagesService.newBookInit(bookData);

    const multiFilesEntries = Object.entries(bookData.multiFiles);
    this._loaderService.pushProgressMax(multiFilesEntries.length * 2);

    await Promise.all(
      multiFilesEntries.map(async ([photoName, data]) => {
        if (data.fileUploadStatus === 10) {
          log.info('loading', photoName);
          await this._multiFileLoad(photoName);
          log.info('loaded', photoName);
        }

        this._loaderService.pushProgressValue(2);
      }),
    );

    this.textChangeSubscribe.next();
    this.messageTextChangeSubscribe.next();

    return bookData;
  }
}
